// File: /src/components/Administration/Planner/EventsPlannerAdmin.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, Button, Space, message, Typography, Row, Col, Select, DatePicker } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PlannerEventTable from "./PlannerEventTable";
import PlannerEventModal from "./PlannerEventModal";
import {
    fetchPlannerEventsAdmin,
    fetchPlannerEventByIdAdmin,
    createPlannerEventAdmin,
    updatePlannerEventAdmin,
    deletePlannerEventAdmin,
    fetchClientsAdmin,
    fetchEventsAdmin
} from "./plannerAdminService";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const EventsPlannerAdmin = () => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [plannerEvents, setPlannerEvents] = useState([]);
    const [clients, setClients] = useState([]);
    const [events, setEvents] = useState([]);

    const [selectedClientId, setSelectedClientId] = useState(undefined);
    const [selectedStatus, setSelectedStatus] = useState(undefined);
    const [selectedRange, setSelectedRange] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingPlannerEvent, setEditingPlannerEvent] = useState(null);

    const loadInitialData = useCallback(async () => {
        try {
            setLoading(true);

            const params = {};

            if (selectedClientId) {
                params.clientID = selectedClientId;
            }

            if (selectedStatus) {
                params.status = selectedStatus;
            }

            if (selectedRange?.length === 2) {
                params.from = dayjs(selectedRange[0]).startOf("day").toISOString();
                params.to = dayjs(selectedRange[1]).endOf("day").toISOString();
            }

            const [plannerEventsData, clientsData, eventsData] = await Promise.all([
                fetchPlannerEventsAdmin(params),
                fetchClientsAdmin(),
                fetchEventsAdmin()
            ]);

            setPlannerEvents(Array.isArray(plannerEventsData) ? plannerEventsData : []);
            setClients(Array.isArray(clientsData) ? clientsData : []);
            setEvents(Array.isArray(eventsData) ? eventsData : []);
        } catch (error) {
            console.error("Error loading planner admin data:", error);
            message.error("Error loading planner data");
        } finally {
            setLoading(false);
        }
    }, [selectedClientId, selectedStatus, selectedRange]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const filteredEventsForModal = useMemo(() => {
        if (!selectedClientId) return events;

        return events.filter(event => {
            const eventClientId = event?.clientID?._id || event?.clientID;
            return eventClientId === selectedClientId;
        });
    }, [events, selectedClientId]);

    const handleOpenCreate = () => {
        setEditingPlannerEvent(null);
        setModalOpen(true);
    };

    const handleOpenEdit = async (record) => {
        try {
            setSubmitting(true);
            const plannerEvent = await fetchPlannerEventByIdAdmin(record._id);
            setEditingPlannerEvent(plannerEvent);
            setModalOpen(true);
        } catch (error) {
            console.error("Error loading planner event:", error);
            message.error("Error loading planner event");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (record) => {
        try {
            setLoading(true);
            await deletePlannerEventAdmin(record._id);
            message.success("Planner event deleted successfully");
            await loadInitialData();
        } catch (error) {
            console.error("Error deleting planner event:", error);
            message.error("Error deleting planner event");
        } finally {
            setLoading(false);
        }
    };

    const handleModalSubmit = async (payload) => {
        try {
            setSubmitting(true);

            if (editingPlannerEvent?._id) {
                await updatePlannerEventAdmin(editingPlannerEvent._id, payload);
                message.success("Planner event updated successfully");
            } else {
                await createPlannerEventAdmin(payload);
                message.success("Planner event created successfully");
            }

            setModalOpen(false);
            setEditingPlannerEvent(null);
            await loadInitialData();
        } catch (error) {
            console.error("Error saving planner event:", error);
            message.error("Error saving planner event");
        } finally {
            setSubmitting(false);
        }
    };

    const handleModalCancel = () => {
        setModalOpen(false);
        setEditingPlannerEvent(null);
    };

    return (
        <div style={{ padding: 20 }}>
            <Row justify="space-between" align="middle" gutter={[16, 16]}>
                <Col>
                    <Title level={3} style={{ margin: 0 }}>
                        Events Planner Admin
                    </Title>
                </Col>

                <Col>
                    <Space wrap>
                        <Button icon={<ReloadOutlined />} onClick={loadInitialData}>
                            Refresh
                        </Button>

                        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
                            New Planner Event
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Card style={{ marginTop: 20 }}>
                <Space wrap size="middle">
                    <Select
                        allowClear
                        placeholder="Filter by client"
                        style={{ width: 220 }}
                        value={selectedClientId}
                        onChange={setSelectedClientId}
                    >
                        {clients.map(client => (
                            <Option key={client._id} value={client._id}>
                                {client.name}
                            </Option>
                        ))}
                    </Select>

                    <Select
                        allowClear
                        placeholder="Filter by status"
                        style={{ width: 180 }}
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                    >
                        <Option value="draft">Draft</Option>
                        <Option value="confirmed">Confirmed</Option>
                        <Option value="completed">Completed</Option>
                        <Option value="cancelled">Cancelled</Option>
                    </Select>

                    <RangePicker
                        value={selectedRange}
                        onChange={setSelectedRange}
                        format="DD-MM-YYYY"
                    />
                </Space>
            </Card>

            <Card title="Planner Events" style={{ marginTop: 20 }}>
                <PlannerEventTable
                    data={plannerEvents}
                    loading={loading}
                    onEdit={handleOpenEdit}
                    onDelete={handleDelete}
                />
            </Card>

            <PlannerEventModal
                open={modalOpen}
                onCancel={handleModalCancel}
                onSubmit={handleModalSubmit}
                plannerEvent={editingPlannerEvent}
                clients={clients}
                events={filteredEventsForModal}
                submitting={submitting}
            />
        </div>
    );
};

export default EventsPlannerAdmin;